import { SelectionState } from '@/features/InvoiceReport/Edit/components/Drawer/OcrReflectionDrawer/hooks/useApplicationFormSelectionManager';
import { PaymentRequestForm } from '@/features/InvoiceReport/Edit/type';
import { useCallback } from 'react';
import { UseFormClearErrors, UseFormSetValue } from 'react-hook-form';

type UseFillPaymentRequestSummaryProps = {
  setValue: UseFormSetValue<PaymentRequestForm>;
  clearErrors: UseFormClearErrors<PaymentRequestForm>;
};

type PaymentRequestFormSummary = Partial<
  Exclude<
    PaymentRequestForm,
    'invoice_transactions' | 'deleteInvoiceTransactions'
  >
>;

export const useFillPaymentRequestSummary = ({
  setValue,
  clearErrors,
}: UseFillPaymentRequestSummaryProps) => {
  // This function is only called by `OcrReflectionDrawer`
  // It only supports fields that can be reflected from OCR.
  // TODO(james): It should either be moved or modified to be more generic.

  const fillPaymentRequestSummary = useCallback(
    (
      summaryProps: PaymentRequestFormSummary,
      options: {
        invoiceRegistrationNumberKey?: string;
        selectionState: SelectionState;
      }
    ) => {
      const { payee, reportForm } = summaryProps;
      const { invoiceRegistrationNumberKey, selectionState } = options;

      if (selectionState.payeeSelected) {
        clearErrors('payee.value');
        setValue('payee.value', payee?.value, { shouldValidate: true });
      }
      if (selectionState.bookDateSelected) {
        clearErrors('payee.bookDate');
        setValue('payee.bookDate', payee?.bookDate, { shouldValidate: true });
      }
      if (selectionState.dueDateSelected) {
        clearErrors('payee.dueDate');
        setValue('payee.dueDate', payee?.dueDate, { shouldValidate: true });
      }
      if (selectionState.instantPayeeNameSelected) {
        clearErrors('payee.instantPayee.name');
        setValue('payee.instantPayee.name', payee?.instantPayee?.name, {
          shouldValidate: true,
        });
      }
      if (selectionState.bankNameSelected) {
        clearErrors('payee.instantPayee.bankId');
        setValue('payee.instantPayee.bankId', payee?.instantPayee?.bankId, {
          shouldValidate: true,
        });
      }
      if (selectionState.bankBranchNameSelected) {
        clearErrors('payee.instantPayee.bankBranchId');
        setValue(
          'payee.instantPayee.bankBranchId',
          payee?.instantPayee?.bankBranchId,
          { shouldValidate: true }
        );
      }
      if (selectionState.bankAccountTypeSelected) {
        clearErrors('payee.instantPayee.accountType');
        setValue(
          'payee.instantPayee.accountType',
          payee?.instantPayee?.accountType,
          { shouldValidate: true }
        );
      }
      if (selectionState.bankAccountNumberSelected) {
        clearErrors('payee.instantPayee.number');
        setValue('payee.instantPayee.number', payee?.instantPayee?.number, {
          shouldValidate: true,
        });
      }
      if (selectionState.bankNameKanaSelected) {
        clearErrors('payee.instantPayee.holderNameKana');
        setValue(
          'payee.instantPayee.holderNameKana',
          payee?.instantPayee?.holderNameKana,
          { shouldValidate: true }
        );
      }

      // TODO: Figure out what these are and if we really need to set them.
      // https://github.com/moneyforward/ap_frontend/pull/1736/files#r2339193158
      if (payee?.reportForm?.bookDate?.reportFormInputId) {
        clearErrors('payee.reportForm.bookDate.reportFormInputId');
        setValue(
          'payee.reportForm.bookDate.reportFormInputId',
          payee?.reportForm?.bookDate?.reportFormInputId,
          { shouldValidate: true }
        );
      }
      if (payee?.reportForm?.dueDate?.reportFormInputId) {
        clearErrors('payee.reportForm.dueDate.reportFormInputId');
        setValue(
          'payee.reportForm.dueDate.reportFormInputId',
          payee?.reportForm?.dueDate?.reportFormInputId,
          { shouldValidate: true }
        );
      }
      if (payee?.reportForm?.payee?.reportFormInputId) {
        clearErrors('payee.reportForm.payee.reportFormInputId');
        setValue(
          'payee.reportForm.payee.reportFormInputId',
          payee?.reportForm?.payee?.reportFormInputId,
          { shouldValidate: true }
        );
      }

      if (
        reportForm &&
        invoiceRegistrationNumberKey &&
        selectionState.invoiceRegistrationNumberSelected
      ) {
        clearErrors(`reportForm.${invoiceRegistrationNumberKey}`);
        setValue(
          `reportForm.${invoiceRegistrationNumberKey}`,
          reportForm?.[invoiceRegistrationNumberKey],
          { shouldValidate: true }
        );
      }
    },
    [setValue, clearErrors]
  );

  return fillPaymentRequestSummary;
};
